import React, { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../utils/cn';

export interface ShareButtonProps {
  onShare: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'destructive';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  showIcon?: boolean;
  children?: React.ReactNode;
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  onShare,
  className,
  variant = 'primary',
  size = 'md',
  showIcon = true,
  children
}) => {
  const [isSharing, setIsSharing] = useState(false);
  const [shareStatus, setShareStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleShare = async () => {
    if (isSharing) return;

    setIsSharing(true);
    setShareStatus('idle');

    try {
      // Call the onShare callback which handles the actual sharing
      // (via GameContext.shareGame() -> useGameLogic.handleShare())
      onShare();

      // Show success feedback
      setShareStatus('success');
    } catch (error) {
      console.error('Share failed:', error);
      setShareStatus('error');
    } finally {
      setIsSharing(false);

      // Reset status after 2 seconds
      setTimeout(() => {
        setShareStatus('idle');
      }, 2000);
    }
  };

  const getButtonContent = () => {
    if (children) {
      return children;
    }

    const getIcon = () => {
      if (!showIcon) return null;

      switch (shareStatus) {
        case 'success':
          return <Check className="h-4 w-4" />;
        case 'error':
          return <Share2 className="h-4 w-4" />;
        default:
          return <Share2 className="h-4 w-4" />;
      }
    };

    const getText = () => {
      switch (shareStatus) {
        case 'success':
          return 'Copied!';
        case 'error':
          return 'Try again';
        default:
          return 'Copy Results to Clipboard';
      }
    };

    const icon = getIcon();
    const text = getText();

    if (size === 'icon') {
      return icon;
    }

    return (
      <span className="flex items-center gap-2">
        {icon}
        {text}
      </span>
    );
  };

  const getButtonVariant = () => {
    if (shareStatus === 'success') {
      return 'primary';
    }
    if (shareStatus === 'error') {
      return 'outline';
    }
    return variant;
  };

  return (
    <Button
      onClick={handleShare}
      disabled={isSharing}
      variant={getButtonVariant()}
      size={size}
      className={cn(
        'transition-all duration-200',
        shareStatus === 'success' && 'bg-green-600 hover:bg-green-700 text-white',
        shareStatus === 'error' && 'border-red-500 text-red-500 hover:bg-red-50',
        isSharing && 'opacity-75 cursor-not-allowed',
        className
      )}
      aria-label={
        shareStatus === 'success'
          ? 'Results copied to clipboard'
          : shareStatus === 'error'
          ? 'Share failed, try again'
          : 'Copy your game results to clipboard'
      }
    >
      {getButtonContent()}
    </Button>
  );
};

export default ShareButton;